import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flame, MapPin, Phone, Plus, Snowflake, Tag, User, UserCheck, X, Zap } from 'lucide-react';
import { useAssignees } from './useAssignees';

/**
 * The "Инфо" tab of the client card.
 *
 * Two audit findings live here:
 *
 *  - the name, phone and city inputs were editable and the PATCH payload did not
 *    include them, so every edit was silently lost. The fields are now a local
 *    draft that is saved on blur (and on Enter), and the parent forwards exactly
 *    those keys;
 *  - "назначение ответственного" was dead code: complete on the server, absent
 *    from the interface. The picker below lists the organization's active members.
 */

interface ChatInfoTabProps {
  activeChat: any;
  chatTags: string[];
  onUpdateChatField: (id: number, fields: Record<string, unknown>) => void;
}

type ContactDraft = { contactName: string; contactPhone: string; contactCity: string };

const draftFrom = (chat: any): ContactDraft => ({
  contactName: chat?.contactName || '',
  contactPhone: chat?.contactPhone || '',
  contactCity: chat?.contactCity || '',
});

export const ChatInfoTab: React.FC<ChatInfoTabProps> = ({ activeChat, chatTags, onUpdateChatField }) => {
  const [draft, setDraft] = useState<ContactDraft>(() => draftFrom(activeChat));
  const [savedField, setSavedField] = useState<keyof ContactDraft | null>(null);
  const { members, canAssign, loading: assigneesLoading, error: assigneesError } = useAssignees();

  // Re-seed only when a different dialog is opened, so a socket update cannot
  // overwrite what the manager is typing right now.
  useEffect(() => { setDraft(draftFrom(activeChat)); }, [activeChat?.id]);

  const commitField = useCallback((field: keyof ContactDraft) => {
    const next = draft[field].trim();
    const current = String(draftFrom(activeChat)[field] || '');
    if (next === current) return;
    // The name is the dialog's title and cannot be blank; an emptied input is
    // treated as "no change" rather than wiping the card.
    if (field === 'contactName' && !next) {
      setDraft(prev => ({ ...prev, contactName: current }));
      return;
    }
    onUpdateChatField(activeChat.id, { [field]: next });
    setSavedField(field);
    window.setTimeout(() => setSavedField(prev => (prev === field ? null : prev)), 1600);
  }, [activeChat, draft, onUpdateChatField]);

  const handleChange = useCallback((field: keyof ContactDraft) => (
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraft(prev => ({ ...prev, [field]: value }));
    }
  ), []);

  const handleKeyDown = useCallback((field: keyof ContactDraft) => (
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitField(field);
        (event.target as HTMLInputElement).blur();
      }
    }
  ), [commitField]);

  const handleBlur = useCallback((field: keyof ContactDraft) => () => commitField(field), [commitField]);

  const setTemp = useCallback((temp: 'hot' | 'warm' | 'cold') => {
    onUpdateChatField(activeChat.id, { temp });
  }, [activeChat.id, onUpdateChatField]);

  const handleAssigneeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = event.target.value;
    onUpdateChatField(activeChat.id, { assignedToId: raw ? Number(raw) : null });
  }, [activeChat.id, onUpdateChatField]);

  const removeTag = useCallback((index: number) => {
    onUpdateChatField(activeChat.id, { tags: chatTags.filter((_, idx) => idx !== index) });
  }, [activeChat.id, chatTags, onUpdateChatField]);

  const addTag = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const inputEl = form.elements.namedItem('newTag') as HTMLInputElement | null;
    const value = inputEl?.value.trim().replace(/^#/, '');
    if (!value) return;
    onUpdateChatField(activeChat.id, { tags: Array.from(new Set([...chatTags, value])) });
    if (inputEl) inputEl.value = '';
  }, [activeChat.id, chatTags, onUpdateChatField]);

  const assignedName = useMemo(() => {
    const assignedId = activeChat?.assignedToId;
    if (!assignedId) return 'Не назначен';
    return members.find(member => member.id === Number(assignedId))?.name || `Сотрудник #${assignedId}`;
  }, [activeChat?.assignedToId, members]);

  const savedMark = (field: keyof ContactDraft) => (
    savedField === field ? <span className="field-saved-mark" role="status"> сохранено</span> : null
  );

  return (
    <div role="tabpanel" id="client-panel-info" aria-labelledby="client-tab-info">
      <div className="client-hero-card" style={{ marginTop: 0 }}>
        <div className="client-form-fields">
          <div className="field-group">
            <label htmlFor="chat-contact-name"><User size={12} /> Имя клиента{savedMark('contactName')}</label>
            <div className="field-input-wrapper">
              <User size={14} className="field-input-icon" />
              <input
                id="chat-contact-name"
                type="text"
                className="field-input"
                value={draft.contactName}
                placeholder="Имя клиента"
                onChange={handleChange('contactName')}
                onBlur={handleBlur('contactName')}
                onKeyDown={handleKeyDown('contactName')}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="chat-contact-phone"><Phone size={12} /> Телефон{savedMark('contactPhone')}</label>
            <div className="field-input-wrapper">
              <Phone size={14} className="field-input-icon" />
              <input
                id="chat-contact-phone"
                type="tel"
                className="field-input"
                value={draft.contactPhone}
                placeholder="+7 (999) 000-00-00"
                onChange={handleChange('contactPhone')}
                onBlur={handleBlur('contactPhone')}
                onKeyDown={handleKeyDown('contactPhone')}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="chat-contact-city"><MapPin size={12} /> Город{savedMark('contactCity')}</label>
            <div className="field-input-wrapper">
              <MapPin size={14} className="field-input-icon" />
              <input
                id="chat-contact-city"
                type="text"
                className="field-input"
                value={draft.contactCity}
                placeholder="Например: Москва"
                onChange={handleChange('contactCity')}
                onBlur={handleBlur('contactCity')}
                onKeyDown={handleKeyDown('contactCity')}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="chat-assignee"><UserCheck size={12} /> Ответственный</label>
            <div className="assignee-row">
              {canAssign ? (
                <select
                  id="chat-assignee"
                  className="assignee-select"
                  value={activeChat?.assignedToId ? String(activeChat.assignedToId) : ''}
                  onChange={handleAssigneeChange}
                  disabled={assigneesLoading || !!assigneesError}
                >
                  <option value="">Не назначен</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name}{member.role === 'OWNER' ? ' — владелец' : member.role === 'ADMIN' ? ' — админ' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="assignee-static">{assignedName}</span>
              )}
            </div>
            {assigneesError && <div className="assignee-hint">{assigneesError}</div>}
            {!assigneesError && !canAssign && (
              <div className="assignee-hint">Менять ответственного могут владелец и администратор.</div>
            )}
          </div>

          <div className="field-group">
            <label><Flame size={12} /> Температура лида</label>
            <div className="temp-chips-grid">
              <button type="button" className={`temp-btn ${activeChat.temp === 'hot' ? 'active hot' : ''}`} onClick={() => setTemp('hot')}><Flame size={13} />Горячий</button>
              <button type="button" className={`temp-btn ${activeChat.temp === 'warm' ? 'active warm' : ''}`} onClick={() => setTemp('warm')}><Zap size={13} />Тёплый</button>
              <button type="button" className={`temp-btn ${activeChat.temp === 'cold' ? 'active cold' : ''}`} onClick={() => setTemp('cold')}><Snowflake size={13} />Холодный</button>
            </div>
          </div>
        </div>
      </div>

      <div className="side-card">
        <div className="side-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Tag size={12} /><span>Теги клиента</span>
        </div>
        <div className="tag-list" style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {chatTags.map((tag: string, i: number) => (
            <span key={tag} className="tag-badge-item">#{tag}
              <button type="button" className="tag-remove-btn" onClick={() => removeTag(i)} title="Удалить тег" aria-label={`Удалить тег ${tag}`}>
                <X size={10} />
              </button>
            </span>
          ))}
          {chatTags.length === 0 && <span className="tag-empty-note">Теги не заданы</span>}
        </div>
        <form onSubmit={addTag} style={{ display: 'flex', gap: '6px' }}>
          <input name="newTag" placeholder="Добавить тег..." className="tag-input-field" aria-label="Новый тег" />
          <button type="submit" className="tag-add-btn" aria-label="Добавить тег"><Plus size={14} /></button>
        </form>
      </div>
    </div>
  );
};
